/**
 * Shared utility for initializing the Airwallex SDK for SCA.
 *
 * This centralizes SDK initialization logic to avoid duplication
 * and ensures we only have one SDK instance.
 *
 * @see https://www.airwallex.com/docs/banking-as-a-service__strong-customer-authentication-(sca)__sca-for-transaction-data-retrieval
 */
import { type Env, type InitResult, type SDKOptions, init } from '@airwallex/components-sdk'

import { authorizeSCA } from './authorizeSCA'
import { generateCodeVerifier } from './codeChallenge'

type InitializeSCASDKParams = {
  orgId: string
  vesselId: string
  connectedAccountId: string
  accessToken: string
  config: {
    clientId: string
    env: Env
  }
}

/**
 * Initializes the Airwallex SDK for SCA operations.
 *
 * This function:
 * 1. Generates a code verifier (PKCE)
 * 2. Gets an auth code from the backend
 * 3. Initializes the SDK with the auth code
 *
 * @returns The initialized SDK result with access to getScaToken()
 */
export async function initializeSCASDK(params: InitializeSCASDKParams): Promise<InitResult> {
  const { orgId, vesselId, connectedAccountId, accessToken, config } = params

  // Step 1: Generate code verifier for PKCE flow
  const codeVerifier = generateCodeVerifier()

  // Step 2: Get auth code from backend
  const authResponse = await authorizeSCA(orgId, vesselId, connectedAccountId, accessToken, codeVerifier)

  if (!authResponse.authCode) {
    throw new Error('Failed to get auth code from backend')
  }

  // Step 3: Initialize SDK with auth code
  const options: SDKOptions = {
    locale: 'en',
    env: config.env,
    enabledElements: ['scaVerify'],
    authCode: authResponse.authCode,
    clientId: config.clientId,
    codeVerifier,
  }

  return init(options)
}
